import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  HttpTransportType,
} from "@microsoft/signalr";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IOpenOrder } from "src/Interfaces/automaticMarketMaking";
import { OrderBook } from "src/Interfaces/orderInterfaces";
import { apiAddress } from "src/Requests/config";

interface OrderBookStreamMessage {
  instrumentId: string;
  lastUpdateTime: string;
  orderBook: OrderBook;
  includesOwnOrders: boolean;
  ownOrders?: IOpenOrder[];
}

interface UseLiveOrderBooksOptions {
  enabled?: boolean;
  instrumentIds?: Array<string | null | undefined>;
  includeOwnOrders?: boolean;
  automaticMarketMakingTaskId?: number;
}

const getInstrumentId = (book?: OrderBook | null) =>
  book?.instrumentId || book?.insId || "";

const getUpdateTimestamp = (book?: OrderBook | null): number => {
  const value = book?.lastUpdateTime || book?.samplingTime;
  if (!value) return Number.NEGATIVE_INFINITY;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : Number.NEGATIVE_INFINITY;
};

export const isStrictlyNewerOrderBookTimestamp = (
  receivedAt: number,
  previousAt?: number,
) =>
  Number.isFinite(receivedAt) &&
  receivedAt > (previousAt ?? Number.NEGATIVE_INFINITY);

// SamplingTime belongs to depth; prices and daily limits can change at the same timestamp.
export const isNewerOrPriceOnlyOrderBook = (
  received?: OrderBook | null,
  previous?: OrderBook | null,
): boolean => {
  const receivedAt = getUpdateTimestamp(received);
  const previousAt = getUpdateTimestamp(previous);
  if (isStrictlyNewerOrderBookTimestamp(receivedAt, previousAt)) return true;
  if (!received || !previous || !Number.isFinite(receivedAt) || receivedAt !== previousAt ||
    getInstrumentId(received) !== getInstrumentId(previous)) return false;
  if ((received.lastPrice ?? null) === (previous.lastPrice ?? null) &&
    (received.closingPrice ?? null) === (previous.closingPrice ?? null) &&
    (received.minPricePermit ?? null) === (previous.minPricePermit ?? null) &&
    (received.maxPricePermit ?? null) === (previous.maxPricePermit ?? null)) return false;

  const rows = received.bestLimitsDto?.bestLimits || [];
  const previousRows = previous.bestLimitsDto?.bestLimits || [];
  const fields = ["number", "insCode", "pMeDem", "pMeOf", "qTitMeDem", "qTitMeOf", "zOrdMeDem", "zOrdMeOf"] as const;
  return rows.length === previousRows.length && rows.every((row, index) =>
    fields.every(field => row[field] === previousRows[index][field]));
};

export const preferNewerOrderBook = (
  polledBook?: OrderBook | null,
  streamedBook?: OrderBook | null,
): OrderBook | undefined =>
  isNewerOrPriceOnlyOrderBook(streamedBook, polledBook)
    ? streamedBook || undefined
    : polledBook || undefined;

const getAccessToken = () => {
  const token = localStorage.getItem("token") || "";
  return token.replace(/^Bearer\s+/i, "");
};

export default function useLiveOrderBooks({
  enabled = false,
  instrumentIds = [],
  includeOwnOrders = false,
  automaticMarketMakingTaskId,
}: UseLiveOrderBooksOptions) {
  const [streamBooks, setStreamBooks] = useState<Record<string, OrderBook>>({});
  const [ownOrders, setOwnOrders] = useState<IOpenOrder[] | undefined>();
  const [connected, setConnected] = useState(false);
  const newestBookRef = useRef<Record<string, OrderBook>>({});
  const wsUpdateSequenceRef = useRef(0);
  const connectionRef = useRef<HubConnection | null>(null);
  const instrumentIdsKey = instrumentIds
    .filter((id): id is string => !!id?.trim())
    .map((id) => id.trim())
    .join("|");
  const normalizedInstrumentIds = useMemo(
    () =>
      Array.from(
        new Set(instrumentIdsKey.split("|").filter(Boolean)),
      ).sort(),
    [instrumentIdsKey],
  );

  useEffect(() => {
    if (!enabled || (!normalizedInstrumentIds.length && !automaticMarketMakingTaskId)) {
      setConnected(false);
      return;
    }

    let disposed = false;
    const connection = new HubConnectionBuilder()
      .withUrl(`${apiAddress()}/hubs/orderbook`, {
        accessTokenFactory: getAccessToken,
        transport: HttpTransportType.WebSockets,
        skipNegotiation: true,
        withCredentials: false,
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000])
      .build();
    connectionRef.current = connection;

    const subscribe = () =>
      connection.invoke("Subscribe", {
        instrumentIds: normalizedInstrumentIds,
        includeOwnOrders,
        automaticMarketMakingTaskId,
      });

    connection.on("OrderBookUpdated", (message: OrderBookStreamMessage) => {
      const instrumentId = message.instrumentId || getInstrumentId(message.orderBook);
      const receivedAt = Date.parse(
        message.lastUpdateTime || message.orderBook?.lastUpdateTime || message.orderBook?.samplingTime || "",
      );
      if (!instrumentId || !Number.isFinite(receivedAt)) return;
      const streamedOrderBook = {
        ...message.orderBook,
        lastUpdateTime: message.lastUpdateTime || message.orderBook?.lastUpdateTime || message.orderBook?.samplingTime,
      };
      if (!isNewerOrPriceOnlyOrderBook(streamedOrderBook, newestBookRef.current[instrumentId])) return;

      newestBookRef.current[instrumentId] = streamedOrderBook;
      streamedOrderBook.wsUpdateSequence = ++wsUpdateSequenceRef.current;
      setStreamBooks((current) => ({
        ...current,
        [instrumentId]: streamedOrderBook,
      }));
      if (message.includesOwnOrders) setOwnOrders(message.ownOrders || []);
    });
    connection.onreconnecting(() => !disposed && setConnected(false));
    connection.onreconnected(() => {
      if (disposed) return;
      setConnected(true);
      void subscribe();
    });
    connection.onclose(() => !disposed && setConnected(false));

    void connection
      .start()
      .then(() => {
        if (disposed) return;
        setConnected(true);
        return subscribe();
      })
      .catch(() => {
        if (!disposed) setConnected(false);
      });

    return () => {
      disposed = true;
      setConnected(false);
      if (connection.state === HubConnectionState.Connected)
        void connection.invoke("Unsubscribe").finally(() => connection.stop());
      else void connection.stop();
      if (connectionRef.current === connection) connectionRef.current = null;
    };
  }, [
    automaticMarketMakingTaskId,
    enabled,
    includeOwnOrders,
    normalizedInstrumentIds,
  ]);

  const resolveOrderBook = useCallback(
    (polledBook?: OrderBook | null, selectedInstrumentId?: string): OrderBook | undefined => {
      const instrumentId = selectedInstrumentId || getInstrumentId(polledBook);
      if (!instrumentId) return polledBook || undefined;

      const matchingPolledBook = getInstrumentId(polledBook) === instrumentId ? polledBook : undefined;
      const polledAt = getUpdateTimestamp(matchingPolledBook);
      if (isStrictlyNewerOrderBookTimestamp(
        polledAt,
        getUpdateTimestamp(newestBookRef.current[instrumentId]),
      ) && matchingPolledBook)
        newestBookRef.current[instrumentId] = matchingPolledBook;

      return preferNewerOrderBook(matchingPolledBook, streamBooks[instrumentId]);
    },
    [streamBooks],
  );

  return { connected, ownOrders, resolveOrderBook };
}
